'use client';

import * as React from 'react';

type ToastVariant = 'default' | 'destructive';

export type Toast = {
  id: string;
  title: string;
  description?: string;
  variant?: ToastVariant;
  /** Optional JSX to render on the toast (e.g., a button/link/etc.). */
  action?: React.ReactNode;
  /** Optional lifetime in ms before auto-dismiss (default 4500). */
  duration?: number;
};

type ToastOptions = Omit<Toast, 'id'>;

type ToastContextValue = {
  toast: (opts: ToastOptions) => void;
  /** Expose current toasts for components that want to read them. */
  toasts: Toast[];
};

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  const toast = React.useCallback((opts: ToastOptions) => {
    const id = Math.random().toString(36).slice(2);
    const next: Toast = {
      id,
      title: opts.title,
      description: opts.description,
      variant: opts.variant ?? 'default',
      action: opts.action,
      duration: opts.duration,
    };
    setToasts((prev) => [...prev, next]);

    const ms = typeof opts.duration === 'number' ? opts.duration : 4500;
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, ms);
  }, []);

  return (
    <ToastContext.Provider value={{ toast, toasts }}>
      {children}

      {/* Simple toaster UI */}
      <div className="fixed bottom-4 right-4 space-y-2 z-50">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`rounded-md p-4 shadow-md text-white max-w-sm ${
              t.variant === 'destructive' ? 'bg-red-600' : 'bg-gray-800'
            }`}
          >
            <p className="font-semibold">{t.title}</p>
            {t.description && (
              <p className="text-sm opacity-90 mt-1">{t.description}</p>
            )}
            {t.action && <div className="mt-3">{t.action}</div>}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}